import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  desa,
  useDesa,
  useEtag,
  type ServeiAdminDto,
  type UsuariInformarDto,
} from './administracio';
import { apiRequest } from './client';
import type { AreaDto, DepartamentDto } from './planificacio';

/** Departament tal com el retorna /departaments (inclou camps que el client no tipa a planificacio). */
export type DepartamentAdmin = DepartamentDto & {
  actiu?: boolean;
  version?: number;
  entraGroupId?: string | null;
};
export type AreaAdmin = AreaDto & { descripcio?: string | null; actiu?: boolean; version?: number };

export type RecursEstructura = 'departaments' | 'arees' | 'serveis';

/**
 * Desa un node de l'estructura (POST si és nou, PUT amb `If-Match` si existeix). L'ETag es llegeix
 * en obrir la fitxa del node, perquè un canvi concurrent doni 412 en lloc de sobreescriure'l.
 */
export function useDesaNode<T extends { id?: number | null }>(
  recurs: RecursEstructura,
  id: number | null | undefined,
) {
  const { etag } = useEtag(`/${recurs}`, id);
  const mutacio = useDesa<T>(`/${recurs}`, [recurs], etag);
  const qc = useQueryClient();
  return {
    ...mutacio,
    mutate: (e: T, opts?: { onSuccess?: (desat: T) => void }) =>
      mutacio.mutate(e, {
        onSuccess: (desat) => {
          // Un servei canviat d'àrea o un node desactivat també canvia les guàrdies i els selectors.
          void qc.invalidateQueries({ queryKey: ['guardies'] });
          opts?.onSuccess?.(desat);
        },
      }),
  };
}

/** Alta d'un node nou (sense ETag). */
export function useCreaNode<T extends { id?: number | null }>(recurs: RecursEstructura) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (e: T) => desa<T>(`/${recurs}`, e),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [recurs] }),
  });
}

/** Modificació d'una persona a informar d'un servei (PUT /serveis/{id}/usuaris-informar/{uiId}). */
export function useEditaUsuariInformar(serveiId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (u: UsuariInformarDto) =>
      apiRequest<UsuariInformarDto>(`/serveis/${serveiId}/usuaris-informar/${u.id}`, {
        method: 'PUT',
        json: u,
      }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['serveis', serveiId, 'usuaris-informar'] }),
  });
}

export type { ServeiAdminDto };
